import api from '../../../services';

const fail = statusCode => Object.assign(new Error('Profile opinion unavailable'), { statusCode });

// The activity endpoint already applies ownership/visibility rules and gives
// the profile's order. Only fetch bodies near the current card, not every
// recording/quote in a long history. Nothing enters the public Dexie buffer.
export async function createProfileFeed(username, signal, snapshot) {
  let userId = snapshot?.userId;
  let ids = snapshot?.ids?.slice();
  if (!ids) {
    const profile = await api.userByUsername({ username }, { signal });
    if (!profile.success || !profile.user) throw fail(profile.statusCode);
    userId = Number(profile.user.id);
    const activity = await api.storyList({ user_id: userId, activity: true }, { signal });
    if (!activity.success || !Array.isArray(activity.list)) throw fail(activity.statusCode);
    ids = [...new Set(activity.list.filter(row => Number(row.id) > 0 && Number(row.scope) >= 1
      && Number(row.scope) <= 7).map(row => Number(row.id)))];
  }
  const cards = new Map(snapshot?.cards || []);
  const requests = new Map();
  const load = id => {
    if (signal.aborted) return Promise.reject(fail(499));
    if (!ids.includes(id)) return Promise.resolve(null);
    if (cards.has(id)) return Promise.resolve(cards.get(id));
    if (requests.has(id)) return requests.get(id);
    const request = (async () => {
      const response = await api.reactionLoad({ id }, { signal });
      if (signal.aborted) throw fail(499);
      if ([403, 404].includes(response.statusCode)) {
        ids = ids.filter(value => value !== id);
        return null;
      }
      if (!response.success || !Array.isArray(response.list)) throw fail(response.statusCode);
      const card = response.list.find(item => Number(item.id) === id && Number(item.user?.id) === userId);
      if (!card || card.active === false) {
        ids = ids.filter(value => value !== id);
        return null;
      }
      cards.set(id, card);
      // Bound full comment/media DTOs independently of the lightweight index.
      while (cards.size > 12) cards.delete(cards.keys().next().value);
      return card;
    })().finally(() => requests.delete(id));
    requests.set(id, request);
    return request;
  };
  const adjacent = (id, step) => ids[ids.indexOf(id) + step] || null;
  return {
    load,
    adjacent,
    // A comment deleted/hidden since the list was opened is skipped without
    // stranding the user or falling back to unrelated public opinions.
    async move(id, step) {
      let next = adjacent(id, step);
      while (next) {
        const card = await load(next);
        if (card) return card;
        next = adjacent(id, step);
      }
      return null;
    },
    prefetch(id) {
      for (const step of [-1, 1, 2, 3]) {
        const next = adjacent(id, step);
        if (next) load(next).catch(() => {}); // Retried on deliberate navigation.
      }
    },
    snapshot: () => ({ userId, ids: ids.slice(), cards: [...cards] }),
    get ids() { return ids.slice(); },
  };
}
